import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Grid,
    Switch,
    FormControlLabel
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { Card } from "react-bootstrap";
import makeStyles from '@mui/styles/makeStyles';

import { Formik, FormikProps, FormikValues } from "formik";
import { Loadable, TextMaskCustom } from '../../../common';
import { getProviderCCN } from '../../../store/api/facilities';
import { useData } from '@pure-ptr/react';
import BillingProviderNPIFieldArray from './BillingProviderNPIFieldArray';
import { getClientAddress } from '../../../store/api/clients';

const useStyles = makeStyles(theme => ({
    textField: {
        width: '100%',
    },
    textFieldWithMinLength: {
        width: '95%',
    },
    root: {
        flexGrow: 1,
    },
    checkboxlabel: {
        marginLeft: 0
    },
    switch: {
        margin: theme.spacing(1),
    },
}));

export default function FacilityDialog(props) {
    const {
        open, handleClose, mode, intl, initialValues, facilityCreatePending,
        facilityUpdatePending,currentClient,getFormattedState,STATES,preferredDashboardClient,
        actions: { facilityCreate, facilityUpdate }
    } = props;

    const [findPoviderCCNIndex, setFindPoviderCCNIndex] = useState(-1);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const { data, isPending } = useData(async () => {
        if(open && findPoviderCCNIndex >= 0) {
            setFindPoviderCCNIndex(-1);
            const values = formikRef.current?.values;
            if(values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI) {
                let payload = {
                    billingProviderNPIs: [values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI],
                    facilityNPI: values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI
                }
                const result = await getProviderCCN(payload);
                formikRef.current?.setFieldValue(`billingProviderNpiRequest.${findPoviderCCNIndex}.providerCCN`, result);
                return result;
            }
            return null;
        }
    }, [findPoviderCCNIndex]);



    const classes = useStyles();
    let shouldReloadGrid = false;

    function handleCallback(reload) {
        handleClose(reload);
    }

    return (
        (<Dialog fullWidth open={open} onClose={() => handleClose(false)} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{mode === 'edit' ? 'Edit Facility' : 'Add Facility'}</DialogTitle>
            <DialogContent dividers>
                <Loadable loading={facilityCreatePending || facilityUpdatePending || isPending}>
                    <Formik
                        innerRef={formikRef}
                        enableReinitialize={true}
                        initialValues={initialValues}
                        validate={values => {
                            const errors: any = {};
                            const npiRegex = /^\d{10}$/;
                            const zipRegex = /^\d{5}(-\d{4})?$/;
                            const ccnRegex = /^[A-Z0-9]{6,13}$/;
                            if (!values.name) {
                                errors.name = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }
                            if (!values.address) {
                                errors.address = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }

                            if (values?.address?.length>500) {
                                errors.address = "Address should not exceed 500 characters.";
                            }

                            if (values?.streetAddress?.length>500) {
                                errors.address = "Street Address should not exceed 500 characters.";
                            }

                             if (!values?.state) {
                                 errors.state = intl.formatMessage({
                                     id: "AUTH.VALIDATION.REQUIRED_FIELD",
                                 });
                             }

                             if (!values?.zip) {
                                 errors.zip = intl.formatMessage({
                                     id: "AUTH.VALIDATION.REQUIRED_FIELD",
                                 });
                             }
                            if (values?.zip && !zipRegex.test(values.zip)) {
                                errors.zip = 'Invalid ZIP code format';
                            }

                             if (!values?.city) {
                                 errors.city = intl.formatMessage({
                                     id: "AUTH.VALIDATION.REQUIRED_FIELD",
                                 });
                             }

                            if (values.phone.replace(/\D/g, '').length !== 0 && !(/^[1-9]\d{9}$/.test(values.phone.replace(/\D/g, '')))) {
                                errors.phone = 'Invalid Phone number';
                            } 

                            if (values?.facilityNPI && !npiRegex.test(values?.facilityNPI )) {
                                errors.facilityNPI = "Facility NPI Must be a 10 Digit Number";
                            }

                            const billingProviderNpiRequestError = values.billingProviderNpiRequest.map((npiRecord, index) => {
                                const npiRecordError = {};
                                let hasError = false;
                                if ((npiRecord.billingProviderNPI !== null && npiRecord.billingProviderNPI !== "") || (npiRecord.providerCCN !== null && npiRecord.providerCCN !== "")) {
                                    if (npiRecord.billingProviderNPI === '') {
                                        npiRecordError['billingProviderNPI'] = intl.formatMessage({
                                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                        });
                                        hasError = true;
                                    } else if (npiRecord.billingProviderNPI && !npiRegex.test(npiRecord.billingProviderNPI)) {
                                        npiRecordError['billingProviderNPI'] = 'NPI must be a 10 Digit Number.'
                                        hasError = true;
                                    }

                                    if (npiRecord.providerCCN && !ccnRegex.test(npiRecord.providerCCN)) {
                                        npiRecordError['providerCCN'] = 'Provider CCN must be 6 to 13 alphanumeric characters.'
                                        hasError = true;
                                    }
                                }
                    
                                if (hasError) {
                                    return npiRecordError;
                                }
                    
                                return false;
                            });

                            const seenKeys = new Map();
                            values.billingProviderNpiRequest.forEach((record, index) => {
                                const key = `${record.billingProviderNPI?.trim() || ''}__${record.providerCCN?.trim() || ''}`;
                                if (key !== '__') {
                                    if (seenKeys.has(key)) {
                                        const duplicateError = billingProviderNpiRequestError[index] || {};
                                        duplicateError['billingProviderNPI'] = "Duplicate record.";
                                        billingProviderNpiRequestError[index] = duplicateError;
                                    } else {
                                        seenKeys.set(key, index);
                                    }
                                }
                            });

                            const filteredBillingProviderNpiRequestErr = billingProviderNpiRequestError.filter(value => Object.keys(value).length > 0);
                            const hasAtLeastOneRecord = values.billingProviderNpiRequest.some(record =>
                                (record.billingProviderNPI && record.billingProviderNPI.trim() !== '') ||
                                (record.providerCCN && record.providerCCN.trim() !== '')
                            );

                            if (filteredBillingProviderNpiRequestErr.length > 0) {
                                errors['billingProviderNpiRequest'] = billingProviderNpiRequestError;
                            }
                            else if (!hasAtLeastOneRecord) {
                                const oneRecordError= {};
                                oneRecordError['billingProviderNPI'] = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                                billingProviderNpiRequestError[0] = oneRecordError;
                                errors['billingProviderNpiRequest'] = billingProviderNpiRequestError;
                            }
                            return errors;
                        }}
                        onSubmit={(values, { setStatus, setSubmitting }) => {
                            shouldReloadGrid = true;
                            let payload = {
                                city:values.city?.trim(),
                                state: typeof values.state === 'object' && values.state !== null ? values.state.displayName : values.state,
                                zip:values.zip,
                                streetAddress:values.streetAddress?.trim(),
                                id: undefined,
                                name: values.name,
                                description: values.description,
                                ...(values.ein?.trim() && values.ein.trim() !== '-' ? { ein: values.ein.trim() } : {}),
                                ...(values.tin?.trim() && values.tin.trim() !== '-' ? { tin: values.tin.trim() } : {}),
                                address: values.address?.trim(),
                                phone: values.phone.replace(/\D/g, '').length === 0 ? '' : values.phone,
                                active: values.active,
                                enableForPricer: values.enableForPricer,
                                facilityNPI: values.facilityNPI,
                                billingProviderNpiRequest: values.billingProviderNpiRequest
                                    .filter(npiRecord => (npiRecord.billingProviderNPI !== null && npiRecord.billingProviderNPI !== "") || (npiRecord.providerCCN !== null && npiRecord.providerCCN !== ""))
                                    .map(item => ({ 
                                        billingProviderNPI: item.billingProviderNPI,
                                        providerCCN: item.providerCCN, 
                                    })) || [],
                            }
                            if (mode === 'create') {
                                facilityCreate(payload, handleCallback, shouldReloadGrid);
                            }
                            else {
                                payload = {
                                    ...payload,
                                    id: values.id,
                                    
                                }
                                facilityUpdate(payload, handleCallback, shouldReloadGrid);
                            }
                        }}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldValue,
                        }) => {

                            const { data ,isPending:isFetchingClientDetails} = useData(async () => {
                                if (!currentClient && !preferredDashboardClient) return;
                                if (!values?.applyClientAddress) {
                                    handleToggleChange(values?.applyClientAddress, null)
                                    return
                                }
                                const result = await getClientAddress(currentClient || preferredDashboardClient);

                                handleToggleChange(values?.applyClientAddress, result)
                                return result
                            }, [currentClient, values?.applyClientAddress]);
                            
                            const handleToggleChange = (applyClientAddress,clientDetails) => {
                                const clientInfo = applyClientAddress ? clientDetails : mode === 'edit' ? { ...initialValues, state: initialValues?.state?.abbreviation } : {};

                                setFieldValue("address", clientInfo?.address || "");
                                setFieldValue("streetAddress", clientInfo?.streetAddress || "");
                                setFieldValue("city", clientInfo?.city || "");
                                setFieldValue("zip", clientInfo?.zip || "");
                                setFieldValue(
                                    "state",
                                    (applyClientAddress || (!applyClientAddress && mode === 'edit'))
                                        ? getFormattedState(clientInfo?.state, STATES)?.displayName || ""
                                        : ""
                                );
                            };

                            return (<form onSubmit={handleSubmit} noValidate autoComplete="off">
                                <div className={classes.root}>
                                    <Grid container>
                                        <Grid item xs={6} md={6}>
                                            <TextField
                                                required
                                                label="Name"
                                                name="name"
                                                className={classes.textFieldWithMinLength}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.name}
                                                helperText={touched.name && errors.name}
                                                error={Boolean(touched.name && errors.name)}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={6}>
                                            <TextField
                                                label="EIN"
                                                name="ein"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.ein}
                                                helperText={touched.ein && errors.ein}
                                                error={Boolean(touched.ein && errors.ein)}
                                                inputProps={{
                                                    mask: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
                                                }}
                                                InputProps={{
                                                    inputComponent: TextMaskCustom,
                                                }}
                                            />
                                        </Grid>
                                        <Grid className="mt-2" item xs={12} md={12}>
                                            <TextField
                                                label="Description"
                                                name="description"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.description}
                                            />
                                        </Grid>
                                        <Loadable loading={isFetchingClientDetails != null} parentClassName={classes.textField}>

                                        <Grid item xs={12} md={12}>
                                            <FormControlLabel control={<Switch onChange={(e) => setFieldValue('applyClientAddress', e.target.checked)} />} label="Apply Client Address" sx={{ mt: "16px" }} />

                                            <TextField
                                                required
                                                multiline
                                                maxRows="3"
                                                label="Street Address Line 1"
                                                name="address"
                                                className={classes.textField}
                                                sx={{"&.MuiFormControl-root":{mt:0}}}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    if (e.target.value.length < 251) {
                                                        handleChange(e)
                                                    }
                                                }
                                                }
                                                value={values.address}
                                                helperText={touched.address && errors.address}
                                                error={Boolean(touched.address && errors.address)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                    
                                         <TextField
                                                multiline
                                                maxRows="3"
                                                label="Street Address Line 2 (Optional)"
                                                name="streetAddress"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    if (e.target.value.length < 251) {
                                                        handleChange(e)
                                                    }
                                                }
                                                }
                                                value={values.streetAddress}
                                                helperText={touched.streetAddress && errors.streetAddress}
                                                error={Boolean(touched.streetAddress && errors.streetAddress)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <Autocomplete
                                                value={values.state || null}
                                                disableClearable={false}
                                                onChange={(e, value) => {
                                                    setFieldValue("state", value?.displayName || null)
                                                }}
                                                isOptionEqualToValue={(option, value) => option?.displayName == value}
                                                options={STATES}
                                                getOptionLabel={(option) => option?.displayName || option}
                                                renderInput={(params) =>
                                                    <TextField {...params}
                                                        name={`state`}
                                                        label="State*"
                                                        error={Boolean(touched.state && errors.state)}
                                                        helperText={touched.state && errors.state}
                                                        margin="normal" />
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                required
                                                multiline
                                                maxRows="3"
                                                label="City"
                                                name="city"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    if (e.target.value.length < 101) {
                                                        handleChange(e)
                                                    }
                                                }}
                                                value={values?.city || ''}
                                                helperText={touched.city && errors.city}
                                                error={Boolean(touched.city && errors.city)}
                                            />
                                          
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                required
                                                label="Zip"
                                                name="zip"
                                                multiline
                                                maxRows="3"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values?.zip || ''}
                                                helperText={touched.zip && errors.zip}
                                                error={Boolean(touched.zip && errors.zip)}
                                            />
                                        </Grid>
                                        </Loadable>

                                        <Grid item xs={6} md={6}>
                                            <TextField
                                                label="Phone"
                                                name="phone"
                                                className={classes.textFieldWithMinLength}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.phone}
                                                helperText={touched.phone && errors.phone}
                                                error={Boolean(touched.phone && errors.phone)}
                                                InputProps={{
                                                    inputComponent: TextMaskCustom,
                                                }}
                                                InputLabelProps={{shrink:true}}

                                            />
                                        </Grid>

                                        <Grid item xs={6} md={6}>
                                            <TextField
                                                type="number"
                                                label="Facility Npi"
                                                name="facilityNPI"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.facilityNPI}
                                                helperText={touched.facilityNPI && errors.facilityNPI}
                                                error={Boolean(touched.facilityNPI && errors.facilityNPI)}
                                            />
                                        </Grid>
                                        <Grid className="mt-2" item xs={6} md={6}>
                                            <FormControlLabel
                                                value={values.active}
                                                control={
                                                    <Switch
                                                        name="active"
                                                        color="primary"
                                                        checked={values.active}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={classes.switch}
                                                        value={values.active}
                                                    />
                                                }
                                                label="Enable facility"
                                            />
                                        </Grid>
                                        <Grid className="mt-2" item xs={6} md={6}>
                                            <FormControlLabel
                                                value={values.enableForPricer}
                                                control={
                                                    <Switch
                                                        name="enableForPricer"
                                                        color="primary"
                                                        checked={values.enableForPricer}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={classes.switch}
                                                        value={values.enableForPricer}
                                                    />
                                                }
                                                label="Enable Pricer"
                                            />
                                        </Grid>

                                        <Grid item xs={6} md={6}>
                                            <TextField
                                                label="TIN"
                                                name="tin"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.tin}
                                                helperText={touched.tin && errors.tin}
                                                error={Boolean(touched.tin && errors.tin)}
                                                inputProps={{
                                                    mask: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
                                                }}
                                                InputProps={{
                                                    inputComponent: TextMaskCustom,
                                                }}
                                            />
                                        </Grid>

                                        <Grid id="billingNPI-div" item xs={12} md={12} className="mt-3">
                                            <Card>
                                            <Card.Header as="h5">Billing Provider NPI Configurations *</Card.Header>
                                            <Card.Body>
                                                <BillingProviderNPIFieldArray
                                                values={values}
                                                errors={errors}
                                                setFieldValue={setFieldValue}
                                                disabled={false}
                                                setFindPoviderCCNIndex={setFindPoviderCCNIndex}
                                                />
                                            </Card.Body>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                    <hr />
                                    <Grid container justifyContent="flex-end">
                                        <Button
                                            onClick={() => handleClose(false)}
                                            variant="contained">
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={`ml-3`}
                                            type="submit"
                                            disabled={facilityCreatePending || facilityUpdatePending || isPending}
                                        >
                                            {(facilityCreatePending || facilityUpdatePending) ? 'Saving Changes' : 'Save'}
                                        </Button>
                                    </Grid>
                                </div>
                            </form>)
                        }}
                    </Formik>
                </Loadable>
            </DialogContent>
        </Dialog>)
    );
}
